// src/components/correios/CorreiosConfigPanel.js
import React, { useState, useEffect } from "react";
import { Save, CheckCircle, AlertCircle, Loader2, Play, Key, MapPin, Package, Eye, EyeOff } from "lucide-react";
import { correiosService } from "../../services/correiosService";

export default function CorreiosConfigPanel() {
  const [formData, setFormData] = useState({
    id: "",
    api_user: "semprejoias",
    api_password: "nu3HiM3QDiUrINyMpCScDN7VQpTEqHg1NXpqBpTM",
    cartao_postagem: "0066249660",
    codigo_administrativo: "13092111",
    remetente_nome: "SEMPRE JOIAS LTDA",
    remetente_cnpj: "17511657000116",
    cep: "03533001",
    logradouro: "Av Waldemar Carlos Pereira",
    numero: "179",
    complemento: "Sala 6",
    bairro: "Vila Matilde",
    cidade: "Sao Paulo",
    uf: "SP",
    ddd_celular: "11",
    celular: "948335927",
    email: "jose@semprejoias.com.br",
    peso_padrao: "100",
    comprimento: "17",
    largura: "5",
    altura: "12",
    servico_padrao: "03298", // PAC
  });

  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success: boolean, msg: string }
  const [showPassword, setShowPassword] = useState(false);

  // Carrega configurações existentes ao montar
  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      try {
        const config = await correiosService.fetchConfig();
        if (config) {
          setFormData({
            id: config.id || "",
            api_user: config.api_user || "",
            api_password: config.api_password || "",
            cartao_postagem: config.cartao_postagem || "",
            codigo_administrativo: config.codigo_administrativo || "",
            remetente_nome: config.remetente_nome || "",
            remetente_cnpj: config.remetente_cnpj || "",
            cep: config.cep || "",
            logradouro: config.logradouro || "",
            numero: config.numero || "",
            complemento: config.complemento || "",
            bairro: config.bairro || "",
            cidade: config.cidade || "",
            uf: config.uf || "",
            ddd_celular: config.ddd_celular || "",
            celular: config.celular || "",
            email: config.email || "",
            peso_padrao: String(config.peso_padrao || "100"),
            comprimento: String(config.comprimento || "17"),
            largura: String(config.largura || "5"),
            altura: String(config.altura || "12"),
            servico_padrao: config.servico_padrao || "03298",
          });
        }
      } catch (err) {
        console.error("Erro ao carregar configurações dos Correios:", err);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const saved = await correiosService.saveConfig(formData);
      if (saved && saved.id) {
        setFormData((prev) => ({ ...prev, id: saved.id }));
      }
      alert("Configurações dos Correios salvas com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar configurações: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    try {
      const token = await correiosService.authenticate(
        formData.api_user,
        formData.api_password,
        formData.cartao_postagem
      );
      if (token) {
        setTestResult({
          success: true,
          msg: "Conexão estabelecida com sucesso! Token gerado e válido por 24 horas.",
        });
      } else {
        setTestResult({
          success: false,
          msg: "A API retornou um token vazio.",
        });
      }
    } catch (err) {
      console.error(err);
      setTestResult({
        success: false,
        msg: err.message || "Erro desconhecido ao testar conexão.",
      });
    } finally {
      setTestingConnection(false);
    }
  };

  if (loading && !formData.id) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-slate-500">
        <Loader2 size={32} className="animate-spin text-blue-600 mb-2" />
        <span className="text-sm font-semibold">Carregando configurações...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in">
      <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Package size={20} className="text-blue-650 text-blue-600" /> Integração com os Correios
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Cadastre as credenciais de homologação/produção e configure os padrões de remessa.
          </p>
        </div>
        <button
          type="button"
          onClick={handleTestConnection}
          disabled={testingConnection}
          className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-black transition-colors disabled:opacity-50"
        >
          {testingConnection ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Play size={14} />
          )}
          TESTAR CONEXÃO
        </button>
      </div>

      <form onSubmit={handleSave} className="p-6 space-y-8">
        {/* Alerta do Resultado de Teste */}
        {testResult && (
          <div
            className={`p-4 rounded-lg flex items-start gap-3 border ${
              testResult.success
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-red-50 border-red-200 text-red-800"
            } animate-slide-in`}
          >
            {testResult.success ? (
              <CheckCircle size={20} className="shrink-0 text-emerald-600" />
            ) : (
              <AlertCircle size={20} className="shrink-0 text-red-650 text-red-600" />
            )}
            <div className="text-xs font-semibold">
              <p className="font-bold">{testResult.success ? "Conexão OK!" : "Falha na Conexão"}</p>
              <p className="mt-0.5 opacity-90">{testResult.msg}</p>
            </div>
          </div>
        )}

        {/* 1. CREDENCIAIS */}
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2 border-b pb-2">
            <Key size={16} className="text-blue-500" /> 1. Credenciais da API
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Usuário API</label>
              <input
                name="api_user"
                value={formData.api_user}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Senha de Acesso</label>
              <div className="relative">
                <input
                  name="api_password"
                  type={showPassword ? "text" : "password"}
                  value={formData.api_password}
                  onChange={handleInputChange}
                  className="w-full p-2.5 pr-10 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Cartão de Postagem</label>
              <input
                name="cartao_postagem"
                value={formData.cartao_postagem}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Código Administrativo</label>
              <input
                name="codigo_administrativo"
                value={formData.codigo_administrativo}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>

        {/* 2. REMETENTE E ENDEREÇO */}
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2 border-b pb-2">
            <MapPin size={16} className="text-blue-500" /> 2. Dados do Remetente
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nome / Razão Social Remetente</label>
              <input
                name="remetente_nome"
                value={formData.remetente_nome}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">CNPJ Remetente</label>
              <input
                name="remetente_cnpj"
                value={formData.remetente_cnpj}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">CEP</label>
              <input
                name="cep"
                value={formData.cep}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Logradouro</label>
              <input
                name="logradouro"
                value={formData.logradouro}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Número</label>
              <input
                name="numero"
                value={formData.numero}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Complemento</label>
              <input
                name="complemento"
                value={formData.complemento}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Bairro</label>
              <input
                name="bairro"
                value={formData.bairro}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Cidade</label>
              <input
                name="cidade"
                value={formData.cidade}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">UF</label>
              <input
                name="uf"
                value={formData.uf}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm uppercase outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={2}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">DDD Celular</label>
              <input
                name="ddd_celular"
                value={formData.ddd_celular}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={2}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Celular</label>
              <input
                name="celular"
                value={formData.celular}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">E-mail</label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>

        {/* 3. PADRÕES DE PACOTE E SERVIÇO */}
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2 border-b pb-2">
            <Package size={16} className="text-blue-500" /> 3. Padrões de Embalagem e Serviço
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Peso Padrão (g)</label>
              <input
                name="peso_padrao"
                type="number"
                value={formData.peso_padrao}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Comprimento (cm)</label>
              <input
                name="comprimento"
                type="number"
                value={formData.comprimento}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Largura (cm)</label>
              <input
                name="largura"
                type="number"
                value={formData.largura}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Altura (cm)</label>
              <input
                name="altura"
                type="number"
                value={formData.altura}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Serviço Padrão</label>
              <select
                name="servico_padrao"
                value={formData.servico_padrao}
                onChange={handleInputChange}
                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                required
              >
                <option value="03298">PAC (03298)</option>
                <option value="03220">SEDEX (03220)</option>
                <option value="03158">SEDEX 10 (03158)</option>
                <option value="03140">SEDEX 12 (03140)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Botão de Envio */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-2.5 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 flex items-center gap-2 shadow-lg shadow-green-200 transition-all transform hover:scale-[1.02] uppercase text-xs tracking-wide disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            Salvar Configurações
          </button>
        </div>
      </form>
    </div>
  );
}
